/** @module store/meter_exchanges */
import * as apiMeterExchanges from '@/api/meter_exchanges'
import logger from '@/logger'
import { formatTimestampToLocal, formatValToScaledWithUnit } from '@/helpers/view_helpers'

const state = () => ({})

const mutations = {
  SET_METER_EXCHANGE_PAIR: (state, { leftMeterExchange, rightMeterExchange }) => {
    if (!(leftMeterExchange instanceof MeterExchange) || !(rightMeterExchange instanceof MeterExchange)) {
      logger.info(
        `Dropping SET_METER_EXCHANGE_PAIR because ${leftMeterExchange} or ${rightMeterExchange} are no MeterExchange`
      )
      return
    }

    if (leftMeterExchange.sensorId !== rightMeterExchange.sensorId) {
      logger.info(
        `Dropping SET_METER_EXCHANGE_PAIR because ${leftMeterExchange} and ${rightMeterExchange} dont have matching sensorIds`
      )
      return
    }

    if (leftMeterExchange.isBegin || !rightMeterExchange.isBegin) {
      logger.info(
        `Dropping SET_METER_EXCHANGE_PAIR because ${leftMeterExchange} isBegin is set or ${rightMeterExchange} isBegin is not set`
      )
      return
    }

    const sensorId = leftMeterExchange.sensorId

    state[sensorId] ||= {}

    state[sensorId][leftMeterExchange.id] = {
      left: leftMeterExchange,
      right: rightMeterExchange
    }
  },
  DELETE_METER_EXCHANGE_PAIR: (state, { sensorId, id }) => {
    if (state[sensorId]?.[id]) {
      delete state[sensorId][id]
    }
    if (state[sensorId] && !Object.entries(state[sensorId]).length) {
      delete state[sensorId]
    }
  }
}

const getters = {
  getFormattedMeterExchangePair: (state, _getters, _rootState, rootGetters) => {
    const locale = rootGetters['user/datetimeLocale'] || 'en'

    return ({ sensorId, id }) => {
      if (!state[sensorId]?.[id]) {
        return
      }
      const sensor = rootGetters['measurements/getSensorById'](sensorId)
      const mtrExPair = new MeterExchangePair({
        sensor,
        left: state[sensorId][id].left,
        right: state[sensorId][id].right
      })
      mtrExPair.setFormattedAttributes({ locale })

      return mtrExPair
    }
  },
  getFormattedMeterExchangePairs: (state, getters, rootState, rootGetters) => {
    // `sorter` has to have `column` and `state` properties
    // `tFilter` has to have `from` and `to` properties (as moment instance)
    return ({ sorter = {}, tFilter = null }) => {
      const meterExchangeList = []
      const locale = rootGetters['user/datetimeLocale'] || 'en'
      const mtrExPairOursideTFilter = (mtrExPair) => {
        return (
          tFilter &&
          !mtrExPair.left.t.isBetween(tFilter.from, tFilter.to) &&
          !mtrExPair.right.t.isBetween(tFilter.from, tFilter.to)
        )
      }

      for (const sensorId in state) {
        const sensor = rootGetters['measurements/getSensorById'](sensorId)
        if (!sensor) continue

        for (const id in state[sensorId]) {
          if (id < 0) continue

          const mtrExPair = new MeterExchangePair({
            sensor,
            left: state[sensorId][id].left,
            right: state[sensorId][id].right
          })

          if (mtrExPairOursideTFilter(mtrExPair)) {
            continue
          }

          mtrExPair.setFormattedAttributes({ locale })
          meterExchangeList.push(mtrExPair)
        }
      }

      const adaptSortNumWrtSorterState = (num) => {
        if (!num) {
          return num
        }

        if (sorter.state === 'desc') {
          return -num
        }

        return num
      }
      const sortItemsBySensorName = (a, b) => {
        if (a.sensorName === b.sensorName) {
          return a.left.t < b.left.t ? -1 : 1
        } else {
          const num = a?.sensorName?.localeCompare(b?.sensorName, undefined, { numeric: true })

          return adaptSortNumWrtSorterState(num)
        }
      }
      const sortByT = (a, b) => {
        let num = 0
        if (a < b) {
          num = -1
        }
        if (a > b) {
          num = 1
        }

        return adaptSortNumWrtSorterState(num)
      }
      const sortItemsByLeftT = (a, b) => {
        return sortByT(a.left.t, b.left.t)
      }
      const sortItemsByRightT = (a, b) => {
        return sortByT(a.right.t, b.right.t)
      }
      const sortMeterExchangeList = () => {
        switch (sorter.column) {
          case 'sensorName':
            meterExchangeList.sort(sortItemsBySensorName)
            break
          case 'left':
            meterExchangeList.sort(sortItemsByLeftT)
            break
          case 'right':
            meterExchangeList.sort(sortItemsByRightT)
            break
        }
        return meterExchangeList
      }

      return sortMeterExchangeList()
    }
  }
}

const actions = {
  getMeterExchanges({ commit }, { ctxt, emsId }) {
    return apiMeterExchanges.getMeterExchanges({ ctxt, emsId }).then((rsp) => {
      const meterExchanges = []

      for (const meterExchangeResource of rsp.payload.data) {
        meterExchanges.push(parseMeterExchangeResource(meterExchangeResource))
      }

      meterExchanges.sort((a, b) => {
        if (a.sensorId === b.sensorId) {
          return a.t < b.t ? -1 : 1
        } else {
          return a.sensorId < b.sensorId ? -1 : 1
        }
      })

      for (let i = 0; i < meterExchanges.length; i++) {
        if (meterExchanges[i].isBegin) continue

        if (meterExchanges[i].sensorId !== meterExchanges[i + 1]?.sensorId) continue

        commit('SET_METER_EXCHANGE_PAIR', {
          leftMeterExchange: meterExchanges[i],
          rightMeterExchange: meterExchanges[i + 1]
        })
        i++
      }

      return meterExchanges
    })
  },
  deleteMeterExchangePair({ state, commit }, { ctxt, emsId, leftId, sensorId }) {
    const left = state[sensorId]?.[leftId]?.left
    const right = state[sensorId]?.[leftId]?.right

    if (!left || !right) {
      logger.warn(
        `Not deleteing meter-exchange pair with left ID ${leftId}, because no pair was found in the local store.`
      )
      return
    }

    return apiMeterExchanges
      .deleteMeterExchangePair({
        ctxt,
        emsId,
        leftId: left.id,
        rightId: right.id
      })
      .then(() => {
        commit('DELETE_METER_EXCHANGE_PAIR', {
          sensorId: left.sensorId,
          id: left.id
        })
      })
  },
  autoSearchMeterExchange({ commit }, { ctxt, emsId, leftT, rightT, sensorId }) {
    return apiMeterExchanges
      .autoSearchMeterExchange({ ctxt, emsId, leftT: leftT.format(), rightT: rightT.format(), sensorId })
      .then((rsp) => {
        return parseAndCommitSingleMeterExchangeResourcePair(commit, rsp.payload.data)
      })
  },
  saveMeterExchangePair({ commit }, { ctxt, emsId, left, right }) {
    return apiMeterExchanges.createMeterExchange({ ctxt, emsId, left, right }).then((rsp) => {
      const meterExchanges = parseAndCommitSingleMeterExchangeResourcePair(commit, [
        rsp?.payload?.['atomic:results']?.[0]?.data,
        rsp?.payload?.['atomic:results']?.[1]?.data
      ])

      commit('DELETE_METER_EXCHANGE_PAIR', {
        sensorId: left.sensorId,
        id: left.id
      })

      return meterExchanges
    })
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}

export function MeterExchange({ id, sensorId, t, val, isBegin }) {
  this.id = parseInt(id)
  this.sensorId = sensorId
  this.t = moment(t).utc()
  this.val = parseFloat(val)
  this.isBegin = isBegin

  this.formattedT = null
  this.formattedVal = null

  this.setFormattedAttributes = ({ locale, unit }) => {
    this.formattedT = formatTimestampToLocal(this.t, { locale })
    this.formattedVal = formatValToScaledWithUnit(this.val, { locale, unit })
  }

  this.clone = () => {
    return new MeterExchange({
      id: this.id,
      sensorId: this.sensorId,
      t: this.t,
      val: this.val,
      isBegin: this.isBegin
    })
  }
}

export function MeterExchangePair({ sensor, left, right }) {
  this.sensorId = sensor?.id
  this.sensorName = sensor.getSensorName()
  this.formattedDelta = null
  this.left = left.clone()
  this.right = right.clone()

  const unit = sensor?.unit

  this.setFormattedAttributes = ({ locale }) => {
    this.left.setFormattedAttributes({ locale, unit })
    this.right.setFormattedAttributes({ locale, unit })
    this.formattedDelta = formatValToScaledWithUnit(this.right.val - this.left.val, { locale, unit })
  }
}

const parseAndCommitSingleMeterExchangeResourcePair = (commit, data) => {
  const meterExchanges = []

  for (const meterExchangeResource of data) {
    meterExchanges.push(parseMeterExchangeResource(meterExchangeResource))
  }

  meterExchanges.sort((a, b) => (a.t < b.t ? -1 : 1))

  if (meterExchanges.length === 2) {
    commit('SET_METER_EXCHANGE_PAIR', {
      leftMeterExchange: meterExchanges[0],
      rightMeterExchange: meterExchanges[1]
    })
  }

  return meterExchanges
}

const parseMeterExchangeResource = (resource) => {
  return new MeterExchange({
    id: resource?.id,
    sensorId: resource?.relationships?.sensor?.data?.id,
    t: resource?.attributes?.t,
    val: resource?.attributes?.val,
    isBegin: resource?.attributes?.isBegin
  })
}
